/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {PercentBadgeIcon} from "@heroicons/react/24/outline";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {currencyNumberFormat, translate} from "utils/utility";
import {useDispatch} from "useDispatch";
import {toast} from "react-toastify";
import {Reservation} from "types/entities/Reservation";
import {CheckIcon} from "@heroicons/react/24/solid";
import {useForm} from "react-hook-form";
import {closeModal} from "helpers/actions";
import ModalScrollableClose from "components/ModalScrollableClose";
import SellerAction from "store/seller/SellerAction";

interface BookingSellerDiscountModalProps {
    packagesCartItem: PackagesCartItem;
    reservation: Reservation;
}

const BookingSellerDiscountModal = (props: BookingSellerDiscountModalProps) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const qty = props.packagesCartItem.drivers_count_adult + props.packagesCartItem.drivers_count_child;
    const basePrice = props.packagesCartItem.price / qty;
    const origPrice = props.packagesCartItem.packageItem?.original_price || 0;
    const priceDiscount = (props.packagesCartItem.packageItem?.original_price || 0) - basePrice;
    const resultPrice = props.packagesCartItem.price || 0;
    const maxDiscount = props.packagesCartItem.packageItem?.original_price || 0;
    const [upsellResultPrice, setUpsellResultPrice] = React.useState<number>(resultPrice / 100 || 0);
    // console.log(1, priceDiscount, basePrice, priceDiscount / basePrice);
    const [upsellPercentDiscount, setUpsellPercentDiscount] = React.useState<number>((1 - basePrice / origPrice) * 100 || 0);
    const [upsellPriceDiscount, setUpsellPriceDiscount] = React.useState<number>(priceDiscount / 100 || 0);
    const inputClass = "flex rounded-md border leading-tight w-full py-1 px-2 h-[30px]";

    const {register, setValue, watch} = useForm({
        defaultValues: {
            basePrice: props.packagesCartItem.price / qty,
            origPrice: props.packagesCartItem.packageItem?.original_price,
            priceDiscount: ((props.packagesCartItem.packageItem?.original_price || 0) - basePrice) / 100,
            resultPrice: props.packagesCartItem.price / 100 / qty,
            percentDiscount: (1 - basePrice / origPrice) * 100 || 0,
        },
    });

    const changeDiscountHandler = () => {
        //this is a simple dispatch on backend
        dispatch(SellerAction.fetchChangeUpsellPriceAndQuantity(props.reservation.id, props.packagesCartItem, "price", upsellResultPrice * 100)).then(r => {
            // dispatch(BookingLoginAction.setReservation(r.reservation));
            // dispatch(SellerAction.setReservation(r.reservation));

            toast.success(t("Upsell changed"));
            dispatch(closeModal());
        });
    };

    const getNewPriceFromDiscounts = (type: "percent" | "price", value) => {
        if (!props.packagesCartItem.packageItem) {
            return upsellResultPrice;
        }

        switch (type) {
            case "percent":
                return (props.packagesCartItem.packageItem?.original_price * ((100 - value) / 100)) / 100;
            case "price":
                return (props.packagesCartItem.packageItem?.original_price - value * 100) / 100;
            default:
                return upsellResultPrice;
        }
    };

    const getOtherPriceFromDiscounts = (type: "percent" | "price", newPrice) => {
        const originalPrice = props.packagesCartItem.packageItem?.original_price;
        if (!props.packagesCartItem.packageItem || !originalPrice) {
            return upsellResultPrice;
        }

        const originalPriceFormatted = originalPrice / 100;
        switch (type) {
            case "percent":
                //we are changing percent but want to know the diff for priceDiscount
                return originalPriceFormatted - newPrice;

            case "price":
                //we are changing price but want to know the diff for percentDiscount
                return 100 - (1 - (originalPriceFormatted - newPrice) / originalPriceFormatted) * 100;
            default:
                return upsellResultPrice;
        }
    };

    const returnNewPrice = (type: "percent" | "price", value) => {
        const newPrice = getNewPriceFromDiscounts(type, value);
        setValue("resultPrice", parseFloat(newPrice.toFixed(2)));

        const otherPrice = getOtherPriceFromDiscounts(type, newPrice);
        console.log(type, otherPrice, newPrice);
        const formattedOtherPrice = parseFloat(otherPrice.toFixed(2));
        switch (type) {
            case "percent":
                return setValue("priceDiscount", formattedOtherPrice);
            // return setUpsellPriceDiscount(otherPrice);
            case "price":
                return setValue("percentDiscount", formattedOtherPrice);

            // return setUpsellPercentDiscount(otherPrice);
            default:
                return upsellResultPrice;
        }
    };

    const onChangePercentDiscountHandler = (newValue: number) => {
        setUpsellPercentDiscount(newValue);
        returnNewPrice("percent", +newValue);
    };

    const onChangePriceDiscountHandler = (newValue: number) => {
        console.log(newValue);
        setUpsellPriceDiscount(newValue);
        returnNewPrice("price", newValue);
    };

    const onChangeResultPriceHandler = (newValue: number) => {
        console.log(newValue);
        setUpsellResultPrice(newValue);
    };

    const watchPercentDiscount = watch("percentDiscount");
    const watchPriceDiscount = watch("priceDiscount");
    const watchResultPrice = watch("resultPrice");

    useEffect(() => {
        onChangePercentDiscountHandler(watchPercentDiscount);
    }, [watchPercentDiscount]);

    useEffect(() => {
        onChangePriceDiscountHandler(watchPriceDiscount);
    }, [watchPriceDiscount]);

    useEffect(() => {
        onChangeResultPriceHandler(watchResultPrice);
        // onChangePriceDiscountHandler(watchResultPrice / qty);
    }, [watchResultPrice]);

    return (
        <CardComponent
            className={"BookingSellerDiscountModal w-full md:min-w-[400px] max-w-[500px] !p-0 border border-gray-200 relative overflow-hidden h-auto max-h-full flex flex-col"}>
            <div
                className={"top-0 z-30 sticky flex flex-row justify-between rounded-tl-lg rounded-tr-lg w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-50 text-gray-900 drop-shadow-sm"}>
                <div className={"flex flex-row items-center"}>
                    <PercentBadgeIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                    <h4 className={"font-medium text-gray-700"}>{t("Discount")}</h4>
                </div>
                <ModalScrollableClose />
            </div>

            <div
                className={"flex w-full flex-col px-3 pt-3 pb-4 bg-white overflow-hidden h-auto rounded-bl-lg rounded-br-lg"}>
                <div className={"flex flex-col overflow-y-auto h-full"}>
                    <h3 className={"font-bold text-lg"}>{t("Additional discount for item")}:</h3>
                    <ul className={"list-disc mb-4 border-b pb-4"}>
                        <li className={"ml-4"}>{props.packagesCartItem.packageItem ? translate(props.packagesCartItem.packageItem.name) : ""}</li>
                    </ul>

                    <h4 className={"font-bold text-lg"}>{t("Item information")}</h4>
                    <div className={"mb-6"}>
                        <table className={"w-full"}>
                            <tbody>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Quantity"}</td>
                                <td className={"text-right"}>{qty}</td>
                            </tr>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Original price"}</td>
                                <td className={"text-right"}>{currencyNumberFormat(props.packagesCartItem.packageItem?.original_price)}</td>
                            </tr>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Current price"}</td>
                                <td className={"text-right"}>{currencyNumberFormat(props.packagesCartItem.packageItem?.price)}</td>
                            </tr>
                            <tr className={"border-b border-dashed"}>
                                <td className={"font-medium"}>{"Current discount"}</td>
                                <td className={"text-right"}>{props.packagesCartItem.packageItem?.discount + "%"}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        className={"Actions grid grid-cols-3 gap-x-2 gap-y-3 border rounded-lg px-3 py-3 shadow bg-gray-50"}>
                        <div className={"cols-span-1"}>
                            <label className={"inline-flex mb-1 font-bold"} htmlFor={"percentDiscount"}>
                                {t("% discount")}
                            </label>
                            <input
                                className={clsx(inputClass)}
                                {...register("percentDiscount")}
                                type="number"
                                min={0}
                                max={100}
                                step={0.1}
                                name="percentDiscount"
                                // onChange={e => onChangePercentDiscountHandler(e.target.value)}
                                // placeholder={upsellPercentDiscount.toFixed(1)}
                            />
                        </div>

                        <div className={"cols-span-1"}>
                            <label className={"inline-flex mb-1 font-bold"} htmlFor={"priceDiscount"}>
                                {t("CHF discount")}
                            </label>
                            <input
                                className={clsx(inputClass)}
                                {...register("priceDiscount")}
                                type="number"
                                min={0}
                                max={maxDiscount / 100}
                                step={0.01}
                                name="priceDiscount"
                                // onChange={e => onChangePriceDiscountHandler(e.target.value)}
                                // placeholder={upsellPriceDiscount.toFixed(2)}
                            />
                        </div>

                        <div className={"cols-span-1"}>
                            <label className={"inline-flex mb-1 font-bold"} htmlFor={"resultPrice"}>
                                {t("Result price")}
                            </label>
                            <input
                                className={clsx(inputClass)}
                                {...register("resultPrice")}
                                type="number"
                                inputMode={"numeric"}
                                min={0}
                                step={0.01}
                                name="resultPrice"
                                // onChange={e => onChangeResultPriceHandler(e.target.value)}
                                // placeholder={upsellResultPrice.toFixed(2)}
                            />
                        </div>

                        <div className={"col-span-3 text-center"}>
                            <button
                                className={clsx(
                                    "border-green-400 bg-green-400 text-white mx-auto flex items-center justify-center font-bold leading-tight py-2 px-4 rounded-md",
                                )}
                                onClick={changeDiscountHandler}
                            >
                                <CheckIcon className={"w-5 h-5 flex-none mr-2"} />
                                <span>{t("Apply discount")}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </CardComponent>
    );
};

export default BookingSellerDiscountModal;
